/* eslint-disable no-console */
/**
 * IntelSearchStorageService
 * Manages localStorage persistence for Intel Search saved searches, history, and projects
 * Per-user storage with device-level persistence
 */

const STORAGE_KEYS = {
  SEARCHES: 'zaphire_intel_searches',
  PINNED: 'zaphire_intel_pinned',
  PROJECTS: 'zaphire_intel_projects',
  RECENT: 'zaphire_intel_recent',
};

const MAX_RECENT_ITEMS = 20; // Keep last 20 recent searches

class IntelSearchStorageService {
  constructor() {
    this.userId = null;
  }

  /**
   * Initialize the service with current user ID
   * @param {number} userId - Current user ID
   */
  init(userId) {
    console.log(
      '🔧 IntelSearchStorageService.init() called with userId:',
      userId
    );
    this.userId = userId;
  }

  /**
   * Get storage key with user ID suffix
   * @param {string} baseKey - Base storage key
   * @returns {string} User-specific storage key
   */
  getUserKey(baseKey) {
    if (!this.userId) {
      console.error(
        '❌ IntelSearchStorageService not initialized with userId!'
      );
      throw new Error('IntelSearchStorageService not initialized with userId');
    }
    const key = `${baseKey}_${this.userId}`;
    console.log('🔑 Generated storage key:', key);
    return key;
  }

  /**
   * Generate unique ID
   * @returns {string} Unique ID
   */
  static generateId() {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ==================== SAVED SEARCHES ====================

  /**
   * Get all saved searches
   * @returns {Array} Array of saved search objects
   */
  getSavedSearches() {
    try {
      const key = this.getUserKey(STORAGE_KEYS.SEARCHES);
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading saved searches:', error);
      return [];
    }
  }

  /**
   * Save a new search
   * @param {Object} search - Search object
   * @param {string} search.name - Search name
   * @param {string} search.description - Search description
   * @param {Object} search.searchPayload - Complete search state (keywords, filters)
   * @param {string} search.projectId - Optional project ID
   * @returns {Object} Saved search object with ID
   */
  saveSearch({ name, description = '', searchPayload, projectId = null }) {
    const searches = this.getSavedSearches();
    const newSearch = {
      id: IntelSearchStorageService.generateId(),
      name,
      description,
      searchPayload,
      projectId,
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString(),
      isFavorite: false,
    };

    searches.push(newSearch);
    this.setSavedSearches(searches);
    return newSearch;
  }

  /**
   * Update an existing search
   * @param {string} id - Search ID
   * @param {Object} updates - Fields to update
   * @returns {Object|null} Updated search or null if not found
   */
  updateSearch(id, updates) {
    const searches = this.getSavedSearches();
    const index = searches.findIndex((s) => s.id === id);
    if (index === -1) return null;

    searches[index] = { ...searches[index], ...updates };
    this.setSavedSearches(searches);
    return searches[index];
  }

  /**
   * Delete a search
   * @param {string} id - Search ID
   * @returns {boolean} Success status
   */
  deleteSearch(id) {
    const searches = this.getSavedSearches();
    const filtered = searches.filter((s) => s.id !== id);
    if (filtered.length === searches.length) return false;

    this.setSavedSearches(filtered);

    // Also remove from pinned if present
    const pinned = this.getPinnedSearches();
    if (pinned.includes(id)) {
      this.setPinnedSearches(pinned.filter((pid) => pid !== id));
    }

    return true;
  }

  /**
   * Duplicate a search
   * @param {string} id - Search ID to duplicate
   * @returns {Object|null} New duplicated search or null if original not found
   */
  duplicateSearch(id) {
    const searches = this.getSavedSearches();
    const original = searches.find((s) => s.id === id);
    if (!original) return null;

    return this.saveSearch({
      name: `${original.name} (Copy)`,
      description: original.description,
      searchPayload: JSON.parse(JSON.stringify(original.searchPayload)),
      projectId: original.projectId,
    });
  }

  /**
   * Move search to a project
   * @param {string} searchId - Search ID
   * @param {string} projectId - Project ID (null to remove from project)
   * @returns {boolean} Success status
   */
  moveSearchToProject(searchId, projectId) {
    return !!this.updateSearch(searchId, { projectId });
  }

  /**
   * Update last used timestamp
   * @param {string} id - Search ID
   */
  updateLastUsed(id) {
    this.updateSearch(id, { lastUsed: new Date().toISOString() });
  }

  /**
   * Set all saved searches
   * @param {Array} searches - Array of search objects
   */
  setSavedSearches(searches) {
    try {
      const key = this.getUserKey(STORAGE_KEYS.SEARCHES);
      console.log(
        '💾 Saving',
        searches.length,
        'searches to localStorage key:',
        key
      );
      localStorage.setItem(key, JSON.stringify(searches));
      console.log('✅ Saved successfully');
    } catch (error) {
      console.error('❌ Error saving searches:', error);
    }
  }

  // ==================== PINNED SEARCHES ====================

  /**
   * Get pinned search IDs (in order)
   * @returns {Array} Array of search IDs
   */
  getPinnedSearches() {
    try {
      const key = this.getUserKey(STORAGE_KEYS.PINNED);
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading pinned searches:', error);
      return [];
    }
  }

  /**
   * Set pinned search IDs
   * @param {Array} pinnedIds - Array of search IDs
   */
  setPinnedSearches(pinnedIds) {
    try {
      const key = this.getUserKey(STORAGE_KEYS.PINNED);
      localStorage.setItem(key, JSON.stringify(pinnedIds));
    } catch (error) {
      console.error('Error saving pinned searches:', error);
    }
  }

  /**
   * Pin a search (adds to bottom of pinned list)
   * @param {string} searchId - Search ID
   * @returns {boolean} Success status
   */
  pinSearch(searchId) {
    const pinned = this.getPinnedSearches();
    if (pinned.includes(searchId)) return false;

    // Add to end of array (bottom of list)
    pinned.push(searchId);
    this.setPinnedSearches(pinned);
    return true;
  }

  /**
   * Unpin a search
   * @param {string} searchId - Search ID
   * @returns {boolean} Success status
   */
  unpinSearch(searchId) {
    const pinned = this.getPinnedSearches();
    const filtered = pinned.filter((id) => id !== searchId);
    if (filtered.length === pinned.length) return false;

    this.setPinnedSearches(filtered);
    return true;
  }

  /**
   * Toggle pin status
   * @param {string} searchId - Search ID
   * @returns {boolean} New pin status (true = pinned, false = unpinned)
   */
  togglePin(searchId) {
    const pinned = this.getPinnedSearches();
    if (pinned.includes(searchId)) {
      this.unpinSearch(searchId);
      return false;
    }
    this.pinSearch(searchId);
    return true;
  }

  /**
   * Reorder pinned searches
   * @param {Array} newOrder - New array of search IDs in desired order
   */
  reorderPinned(newOrder) {
    this.setPinnedSearches(newOrder);
  }

  // ==================== PROJECTS ====================

  /**
   * Get all projects
   * @returns {Array} Array of project objects
   */
  getProjects() {
    try {
      const key = this.getUserKey(STORAGE_KEYS.PROJECTS);
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading projects:', error);
      return [];
    }
  }

  /**
   * Create a new project
   * @param {string} name - Project name
   * @param {string} description - Project description
   * @returns {Object} New project object
   */
  createProject(name, description = '') {
    const projects = this.getProjects();
    const newProject = {
      id: IntelSearchStorageService.generateId(),
      name,
      description,
      createdAt: new Date().toISOString(),
      isExpanded: true,
    };

    projects.push(newProject);
    this.setProjects(projects);
    return newProject;
  }

  /**
   * Update a project
   * @param {string} id - Project ID
   * @param {Object} updates - Fields to update
   * @returns {Object|null} Updated project or null if not found
   */
  updateProject(id, updates) {
    const projects = this.getProjects();
    const index = projects.findIndex((p) => p.id === id);
    if (index === -1) return null;

    projects[index] = { ...projects[index], ...updates };
    this.setProjects(projects);
    return projects[index];
  }

  /**
   * Delete a project and optionally its searches
   * @param {string} id - Project ID
   * @param {boolean} deleteSearches - Whether to delete associated searches
   * @returns {boolean} Success status
   */
  deleteProject(id, deleteSearches = false) {
    const projects = this.getProjects();
    const filtered = projects.filter((p) => p.id !== id);
    if (filtered.length === projects.length) return false;

    this.setProjects(filtered);

    if (deleteSearches) {
      // Delete all searches in this project
      const searches = this.getSavedSearches();
      const filteredSearches = searches.filter((s) => s.projectId !== id);
      this.setSavedSearches(filteredSearches);
    } else {
      // Remove project association from searches
      const searches = this.getSavedSearches();
      const updatedSearches = searches.map((search) =>
        search.projectId === id ? { ...search, projectId: null } : search
      );
      this.setSavedSearches(updatedSearches);
    }

    return true;
  }

  /**
   * Toggle project expand/collapse
   * @param {string} id - Project ID
   * @returns {boolean} New expanded state
   */
  toggleProjectExpanded(id) {
    const project = this.getProjects().find((p) => p.id === id);
    if (!project) return false;

    const newState = !project.isExpanded;
    this.updateProject(id, { isExpanded: newState });
    return newState;
  }

  /**
   * Get searches for a project
   * @param {string} projectId - Project ID
   * @returns {Array} Array of search objects
   */
  getProjectSearches(projectId) {
    const searches = this.getSavedSearches();
    return searches.filter((s) => s.projectId === projectId);
  }

  /**
   * Set all projects
   * @param {Array} projects - Array of project objects
   */
  setProjects(projects) {
    try {
      const key = this.getUserKey(STORAGE_KEYS.PROJECTS);
      console.log(
        '💾 Saving',
        projects.length,
        'projects to localStorage key:',
        key
      );
      localStorage.setItem(key, JSON.stringify(projects));
      console.log('✅ Saved successfully');
    } catch (error) {
      console.error('❌ Error saving projects:', error);
    }
  }

  // ==================== RECENT SEARCHES ====================

  /**
   * Get recent searches
   * @returns {Array} Array of recent search payloads
   */
  getRecentSearches() {
    try {
      const key = this.getUserKey(STORAGE_KEYS.RECENT);
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading recent searches:', error);
      return [];
    }
  }

  /**
   * Compare two search payloads for equality
   * @param {Object} payload1 - First search payload
   * @param {Object} payload2 - Second search payload
   * @returns {boolean} True if payloads are equal
   */
  static arePayloadsEqual(payload1, payload2) {
    // Normalize payloads: sort object keys but preserve array order
    const normalize = (obj) => {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }
      if (Array.isArray(obj)) {
        // Preserve array order - don't sort arrays
        return obj.map((item) =>
          typeof item === 'object' && item !== null ? normalize(item) : item
        );
      }
      // Sort object keys for consistent comparison
      const sorted = {};
      Object.keys(obj)
        .sort()
        .forEach((key) => {
          sorted[key] = normalize(obj[key]);
        });
      return sorted;
    };

    try {
      const normalized1 = normalize(payload1);
      const normalized2 = normalize(payload2);
      return JSON.stringify(normalized1) === JSON.stringify(normalized2);
    } catch (error) {
      // Fallback to simple string comparison if normalization fails
      return JSON.stringify(payload1) === JSON.stringify(payload2);
    }
  }

  /**
   * Add to recent searches
   * @param {Object} searchPayload - Search payload
   */
  addToRecent(searchPayload) {
    try {
      const recent = this.getRecentSearches();

      // Remove any existing duplicate entries (same search payload)
      const filtered = recent.filter(
        (item) =>
          !IntelSearchStorageService.arePayloadsEqual(
            item.searchPayload,
            searchPayload
          )
      );

      // Create a recent entry
      const entry = {
        id: IntelSearchStorageService.generateId(),
        searchPayload,
        timestamp: new Date().toISOString(),
      };

      // Add to beginning (most recent first)
      filtered.unshift(entry);

      // Keep only MAX_RECENT_ITEMS
      const trimmed = filtered.slice(0, MAX_RECENT_ITEMS);

      const key = this.getUserKey(STORAGE_KEYS.RECENT);
      localStorage.setItem(key, JSON.stringify(trimmed));
    } catch (error) {
      console.error('Error adding to recent searches:', error);
    }
  }

  /**
   * Clear all recent searches
   */
  clearRecent() {
    try {
      const key = this.getUserKey(STORAGE_KEYS.RECENT);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing recent searches:', error);
    }
  }

  // ==================== UTILITIES ====================

  /**
   * Clear all data for current user
   */
  clearAll() {
    Object.values(STORAGE_KEYS).forEach((baseKey) => {
      try {
        const key = this.getUserKey(baseKey);
        localStorage.removeItem(key);
      } catch (error) {
        console.error(`Error clearing ${baseKey}:`, error);
      }
    });
  }

  /**
   * Export all data as JSON
   * @returns {Object} All data
   */
  exportData() {
    return {
      searches: this.getSavedSearches(),
      pinned: this.getPinnedSearches(),
      projects: this.getProjects(),
      recent: this.getRecentSearches(),
    };
  }

  /**
   * Import data from JSON
   * @param {Object} data - Data object
   */
  importData(data) {
    if (data.searches) this.setSavedSearches(data.searches);
    if (data.pinned) this.setPinnedSearches(data.pinned);
    if (data.projects) this.setProjects(data.projects);
    if (data.recent) {
      const key = this.getUserKey(STORAGE_KEYS.RECENT);
      localStorage.setItem(key, JSON.stringify(data.recent));
    }
  }
}

// Export singleton instance
export default new IntelSearchStorageService();
